import React, { useCallback, useMemo, useState } from 'react';
import Button, { LoadingButton } from '@atlaskit/button';
import { Panel, TaskGap, TaskLayout } from '@atlassian/commerce-ui/layout';
import SectionMessage from '@atlaskit/section-message';
import { CreditCardErrorMessage, CreditCardForm } from '@atlassian/commerce-ui/credit-card-ccp';
import { CommerceVisaLegalNote } from '@atlassian/commerce-ui/legal-notes';
import { SUBSCRIPTION_CHARGE_NOTE as SUBSCRIPTION_CHARGE_NOTE_V2 } from '@css-xp/ppc-constants';
import { PaymentMethodFormProps, PaymentMethodOperation } from './types';
import { InfoPanel } from '../../info-panel';
import { AssociatedBillingProfiles } from '../../associated-billing-profiles/src/associated-billing-profiles';
import { PaymentMethodTypeButtons } from './payment-method-type-buttons';
import { PaymentMethodTypeV2 } from '@css-xp/graphtypes/src/generated-types-new';
// eslint-disable-next-line workspaces/require-dependency
import {
  AuthorizationResponse,
  CreatePaymentMethodContext,
  PaypalButton,
  PaypalButtonContainer,
  PaypalLoginPanel,
} from '@css-xp/ppc-paypal';
import './payment-method-form.scss';
import { StripePaymentElements } from './stripe-element';

export const SUBSCRIPTION_CHARGE_NOTE =
  "You won't be charged immediately. In most cases, you'll be charged for the subscription on the first day of each billing period using the payment method you've specified above. Atlassian will keep your payment info securely on file.";

const defaultSubHeader = 'We accept all major debit/credit cards. For help with other ways to pay, contact us.';

export const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
  operation,
  onCancel,
  isOperationInProgress,
  isPaypalOperationInProgress,
  onSubmit,
  onPaypalSubmit,
  sidebarClassName = '',
  showSubscriptionChargeNote,
  header = `${operation} payment method`,
  subHeader = defaultSubHeader,
  version,
  isLoadingBillingProfiles,
  billingProfiles,
  txaId,
  editPaymentMethod,
  paypalEmail,
  isPaypalSelectionEnabled = false,
  paymentMethod,
  payPalEmails,
  shouldShowACHPaymentMethod = false,
  onUpdateDone,
  onUpdateFailure,
  onUpdateSuccess,
}) => {
  const isVersion2 = version === 2;
  const isReplacingPaypal =
    isPaypalSelectionEnabled &&
    operation === PaymentMethodOperation.REPLACE &&
    paymentMethod === PaymentMethodTypeV2.PAYPAL;

  const [createPaymentMethodContext, setCreatePaymentMethodContext] = useState<CreatePaymentMethodContext | null>(null);
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<PaymentMethodTypeV2>(
    isReplacingPaypal ? PaymentMethodTypeV2.PAYPAL : PaymentMethodTypeV2.CARD
  );
  const [paypalAuthorizedState, setPaypalAuthorizedState] = useState<CreatePaymentMethodContext | null>(null);
  const [isPaypalButtonRendered, setPaypalButtonRendered] = useState(false);

  const isPaypalEmailAlreadyAvailable = React.useRef(false);

  React.useEffect(() => {
    const newPayPalEmail = paypalAuthorizedState?.paypal?.paypalDetails.details.email;
    if (newPayPalEmail) {
      isPaypalEmailAlreadyAvailable.current = payPalEmails?.includes(newPayPalEmail) ?? false;
    }
  }, [paypalAuthorizedState, payPalEmails]);

  const doneButtonLabel = useMemo(() => {
    if (isVersion2 && operation === PaymentMethodOperation.REPLACE) {
      return 'Replace';
    }

    if (isVersion2) {
      return 'Add';
    }

    return 'Confirm';
  }, [operation, isVersion2]);

  const gapComponentClassName = useMemo(() => (isVersion2 ? 'task-gap' : undefined), [isVersion2]);

  const tokenizePaypalPaymentMethod = useCallback(
    (paypalPayload: AuthorizationResponse): void => {
      const paypalAuthorizedPayload: CreatePaymentMethodContext = {
        paymentMethodType: PaymentMethodTypeV2.PAYPAL,
        paypal: {
          paypalDetails: paypalPayload,
        },
      };
      setCreatePaymentMethodContext(paypalAuthorizedPayload);
      setPaypalAuthorizedState(paypalAuthorizedPayload);
    },
    [setCreatePaymentMethodContext]
  );

  const onPaypalApprove = useCallback(
    ({ paypalPayload }: { paypalPayload: AuthorizationResponse }): void => {
      tokenizePaypalPaymentMethod(paypalPayload);
    },
    [tokenizePaypalPaymentMethod]
  );

  const onPaypalButtonRender = useCallback((): void => {
    setPaypalButtonRendered(true);
  }, []);

  const onPaymentMethodTypeSelection = (paymentMethodType: PaymentMethodTypeV2): void => {
    setSelectedPaymentMethodType(paymentMethodType);
    setCreatePaymentMethodContext({
      paymentMethodType,
      paypal: paypalAuthorizedState?.paypal || undefined,
    });

    if (paymentMethodType !== PaymentMethodTypeV2.PAYPAL) {
      // reset paypal button render status
      setPaypalButtonRendered(false);
    }
  };

  const creditCardComponent = (
    <>
      <TaskGap className={gapComponentClassName} />
      {isVersion2 ? (
        <CreditCardForm className="credit-card-form-container" frameEmbeddingMitigation={true} />
      ) : (
        <Panel>
          <CreditCardForm frameEmbeddingMitigation={true} />
        </Panel>
      )}
      {isVersion2 && !isLoadingBillingProfiles && billingProfiles?.length ? (
        <>
          <br />
          <br />
          <InfoPanel
            type="warning"
            message="All billing profiles that use this card will also get updated. See the list below:"
          />
        </>
      ) : null}
      {isVersion2 && operation === PaymentMethodOperation.REPLACE && (
        <>
          <TaskGap className={gapComponentClassName} />
          <AssociatedBillingProfiles
            isLoadingBillingProfiles={isLoadingBillingProfiles}
            billingProfiles={billingProfiles}
            txaId={txaId}
          />
        </>
      )}
      <TaskGap className={gapComponentClassName} />
      {isVersion2 && <br />}
      {showSubscriptionChargeNote && (
        <div className="commerce-subscription-note">
          <p>{version === 2 ? SUBSCRIPTION_CHARGE_NOTE_V2 : SUBSCRIPTION_CHARGE_NOTE}</p>
        </div>
      )}
      <CommerceVisaLegalNote />
    </>
  );

  const paypalComponent = (
    <>
      <PaypalLoginPanel
        emailID={
          editPaymentMethod && !paypalAuthorizedState
            ? paypalEmail
            : paypalAuthorizedState?.paypal?.paypalDetails.details.email
        }
        isEditFlow={editPaymentMethod}
      />
      {isPaypalEmailAlreadyAvailable.current && (
        <div className="paypal-method-available-warning">
          <SectionMessage appearance="error">
            The PayPal account is already linked to this transaction account. Please add a different account.
          </SectionMessage>
        </div>
      )}
      {(!paypalAuthorizedState || isPaypalEmailAlreadyAvailable.current) && (
        <PaypalButtonContainer>
          <PaypalButton
            parentElementId="paypal-button"
            callbacks={{
              onPaypalApprove,
              onPaypalButtonRender,
            }}
          />
        </PaypalButtonContainer>
      )}
    </>
  );

  const isConfirmDisabled =
    isPaypalSelectionEnabled && selectedPaymentMethodType === PaymentMethodTypeV2.PAYPAL
      ? isPaypalOperationInProgress ||
        isOperationInProgress ||
        !isPaypalButtonRendered ||
        isPaypalEmailAlreadyAvailable.current
      : isOperationInProgress;

  return (
    <div
      className={`${sidebarClassName} ${
        selectedPaymentMethodType === PaymentMethodTypeV2.ACH ? 'payment-method-ach-container' : ''
      }`}
    >
      <TaskLayout
        title={isVersion2 ? '' : header}
        testId={`ppc.payment-methods--${operation.toLowerCase()}`}
        actions={
          // Since we are using Stripe provider for ACH, all hooks are isolated in StripePaymentElements component
          // hence rendering footer and handling associated actions there
          selectedPaymentMethodType === PaymentMethodTypeV2.CARD ||
          selectedPaymentMethodType === PaymentMethodTypeV2.PAYPAL ? (
            <>
              <Button onClick={onCancel}>Cancel</Button>
              <LoadingButton
                testId={`ppc.payment-methods--${operation.toLowerCase()}.confirm`}
                appearance="primary"
                onClick={
                  selectedPaymentMethodType === PaymentMethodTypeV2.CARD
                    ? onSubmit
                    : onPaypalSubmit && (() => onPaypalSubmit(txaId, createPaymentMethodContext))
                }
                isLoading={isPaypalOperationInProgress || isOperationInProgress}
                isDisabled={isConfirmDisabled}
              >
                {doneButtonLabel}
              </LoadingButton>
            </>
          ) : null
        }
        actionError={<CreditCardErrorMessage />}
      >
        <p>{subHeader}</p>
        {isPaypalSelectionEnabled && operation !== PaymentMethodOperation.REPLACE && (
          <PaymentMethodTypeButtons
            setSelectedPaymentMethodTypeCallback={onPaymentMethodTypeSelection}
            existingPaymentMethod={PaymentMethodTypeV2.CARD}
            shouldShowACH={shouldShowACHPaymentMethod}
          />
        )}
        {selectedPaymentMethodType === PaymentMethodTypeV2.CARD ? creditCardComponent : null}
        {isPaypalSelectionEnabled && selectedPaymentMethodType === PaymentMethodTypeV2.PAYPAL && paypalComponent}
        {shouldShowACHPaymentMethod && selectedPaymentMethodType === PaymentMethodTypeV2.ACH && (
          <StripePaymentElements
            txaId={txaId}
            selectedPaymentMethodType={selectedPaymentMethodType}
            onUpdateDone={onUpdateDone}
            onUpdateFailure={onUpdateFailure}
            onUpdateSuccess={onUpdateSuccess}
            onCancel={onCancel}
          />
        )}
      </TaskLayout>
    </div>
  );
};
